/**
 * Product Placement Guide Component
 *
 * Reusable detailed materials breakdown by surface.
 * Used in both EstimateResults (wizard) and SharedEstimate (saved link).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { EstimateOutput, MaterialItem, WallConfig, Currency } from '../../types';
import { Map, SquareStack, DoorOpen } from 'lucide-react';
import { getCustomFieldConfig, getCustomFieldsForProduct, getOutputLayout } from '../../data/productTable';
import { buildPlacementTable, pickLayout, RenderCtx } from '../../utils/renderModel';
import { surfaceLabel } from '../../data/bomSections';
import { RenderTable } from './RenderTable';

interface PlacementGuideProps {
  estimateResult: EstimateOutput;
}

const aggregateMaterials = (items: MaterialItem[]): MaterialItem[] => {
  const aggregated: Record<string, MaterialItem> = {};
  items.forEach(item => {
    const key = item.productID;
    if (aggregated[key]) {
      aggregated[key].quantity += item.quantity;
    } else {
      aggregated[key] = { ...item };
    }
  });
  return Object.values(aggregated);
};

// Placement tables never show prices; currency is unused but required by ctx.
const placementCtx: RenderCtx = {
  currency: 'USD' as Currency,
  showPrices: false,
  getFields: getCustomFieldsForProduct,
};

const PanelsTable: React.FC<{ panels: MaterialItem[]; keyPrefix: string }> = ({ panels }) => {
  const { t } = useTranslation('results');
  if (panels.length === 0) return null;
  const table = buildPlacementTable(
    aggregateMaterials(panels), true, placementCtx, getCustomFieldConfig(),
    pickLayout(getOutputLayout(), 'placementPanels'),
  );
  return (
    <div className="placement-material-group">
      <h4 className="placement-group-title">{t('placement.panels')}</h4>
      <RenderTable table={table} ctx={placementCtx} />
    </div>
  );
};

const TrimsTable: React.FC<{ trims: MaterialItem[]; keyPrefix: string }> = ({ trims }) => {
  const { t } = useTranslation('results');
  if (trims.length === 0) return null;
  const table = buildPlacementTable(
    aggregateMaterials(trims), false, placementCtx, getCustomFieldConfig(),
    pickLayout(getOutputLayout(), 'placementTrims'),
  );
  return (
    <div className="placement-material-group">
      <h4 className="placement-group-title">{t('placement.trims')}</h4>
      <RenderTable table={table} ctx={placementCtx} />
    </div>
  );
};

export const PlacementGuide: React.FC<PlacementGuideProps> = ({ estimateResult }) => {
  const { t } = useTranslation('results');
  return (
    <div className="placement-guide">
      <h2 className="section-title">
        <Map size={22} className="section-title-icon" />
        {t('placement.title')}
      </h2>
      <p className="section-description">{t('placement.description')}</p>

      <div className="placement-surfaces">
        {/* Ceiling Section(s) */}
        {estimateResult.surfaces.ceilingZones && estimateResult.surfaces.ceilingZones.length > 0 ? (
          estimateResult.surfaces.ceilingZones.map((zone, zoneIdx) => (
            <div key={`ceiling-zone-${zoneIdx}`} className="placement-surface-section">
              <h3 className="placement-surface-title">
                <SquareStack size={18} />
                {surfaceLabel(zone)}
                <span className="surface-dimensions">
                  {t('placement.dimensionsLW', {
                    length: (zone.config as any)?.zoneLength || '?',
                    width: (zone.config as any)?.zoneWidth || '?',
                  })}
                </span>
              </h3>
              <PanelsTable panels={zone.materials.panels} keyPrefix={`zone-${zoneIdx}`} />
              <TrimsTable trims={zone.materials.trims} keyPrefix={`zone-${zoneIdx}`} />
            </div>
          ))
        ) : estimateResult.surfaces.ceiling ? (
          <div className="placement-surface-section">
            <h3 className="placement-surface-title">
              <SquareStack size={18} />
              {surfaceLabel(estimateResult.surfaces.ceiling)}
              <span className="surface-dimensions">
                {/* In wall-by-wall mode the ceiling has its own dimensions (a single
                    zone); roomDimensions is an unused default there. Prefer the zone. */}
                {t('placement.dimensionsLW', {
                  length: estimateResult.input.ceilingZones?.[0]?.length ?? estimateResult.input.roomDimensions.length,
                  width: estimateResult.input.ceilingZones?.[0]?.width ?? estimateResult.input.roomDimensions.width,
                })}
              </span>
            </h3>
            <PanelsTable panels={estimateResult.surfaces.ceiling.materials.panels} keyPrefix="ceiling" />
            <TrimsTable trims={estimateResult.surfaces.ceiling.materials.trims} keyPrefix="ceiling" />
          </div>
        ) : null}

        {/* Wall Sections */}
        {estimateResult.surfaces.walls.map((wall, wallIdx) => {
          const wallConfig = wall.config as WallConfig;
          const hasGarage = wallConfig.garage?.enabled && wallConfig.garage?.numDoors > 0;
          const wallLength = wallConfig.length || (wallIdx % 2 === 0 ? estimateResult.input.roomDimensions.length : estimateResult.input.roomDimensions.width);
          const wallHeight = wallConfig.height || estimateResult.input.roomDimensions.height;

          return (
            <div key={wall.surfaceId} className="placement-surface-section">
              <h3 className="placement-surface-title">
                <SquareStack size={18} />
                {surfaceLabel(wall)}
                {hasGarage && (
                  <span className="garage-door-info">
                    <DoorOpen size={16} />
                    {t('placement.garageDoors', {
                      count: wallConfig.garage.numDoors,
                      width: wallConfig.garage.width,
                      height: wallConfig.garage.height,
                    })}
                  </span>
                )}
                <span className="surface-dimensions">
                  {t('placement.dimensionsLH', { length: wallLength, height: wallHeight })}
                </span>
              </h3>
              <PanelsTable panels={wall.materials.panels} keyPrefix={`wall-${wallIdx}`} />
              <TrimsTable trims={wall.materials.trims} keyPrefix={`wall-${wallIdx}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlacementGuide;
